// client/src/components/plataforma/planosContas/PlansContasFormPage.js (VERSÃO 100% CORRIGIDA)
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useContaPlanById, useMutateContaPlans, useAccountsForPlan, usePlanFeatures } from '../../../hooks/useContaPlans';
import { useAuth } from '../../../context/auth/AuthProvider';
import {
  Loader2, Save, Package, CheckSquare, ListChecks, Users,
  CreditCard, Ticket, Sparkles, Zap, Shield, Globe, Database, Cpu,
  ShoppingCart, Tags, MessageSquare, FileText, Clock, DollarSign,
  BarChart3, Mail, Smartphone, Coins, Cloud, Code, Palette, Headphones,
  Calendar, Truck, Image, MapPin, GraduationCap, Home, AlertCircle,
  Star, Hourglass, Receipt, MessageCircle
} from 'lucide-react';
import PageHeader from '../../layout/PageHeader';
import FabSaveButton from '../../common/FabSaveButton';

// Componente LimitInput
const LimitInput = ({ label, name, feature, onValueChange, onUnlimitedChange, disabled }) => { 
  const safeFeature = feature || { value: 0, isUnlimited: false };
  
  return (
    <div className="form-group col-span-2 md:col-span-1"> 
      <label htmlFor={name}>{label}</label> 
      <div className="flex items-center gap-2"> 
        <input 
          type="number" 
          id={name} 
          name={name} 
          value={safeFeature.isUnlimited ? '' : safeFeature.value} 
          onChange={onValueChange} 
          className="form-input" 
          disabled={disabled || safeFeature.isUnlimited} 
          min="0" 
        /> 
        <div className="flex items-center"> 
          <input 
            type="checkbox" 
            id={`unlimited-${name}`} 
            checked={safeFeature.isUnlimited} 
            onChange={onUnlimitedChange} 
            className="form-checkbox-sm" 
            disabled={disabled} 
          /> 
          <label htmlFor={`unlimited-${name}`} className="text-xs ml-1 whitespace-nowrap">
            Ilimitado
          </label> 
        </div> 
      </div> 
    </div> 
  );
};

// Componente FeatureToggle
const FeatureToggle = ({ label, name, checked, onChange, disabled, icon: Icon }) => ( 
  <div className="form-group-checkbox items-center col-span-1"> 
    <input 
      type="checkbox" 
      name={name} 
      id={name} 
      checked={!!checked}
      onChange={onChange} 
      className="form-checkbox" 
      disabled={disabled} 
    /> 
    <label htmlFor={name} className="flex items-center gap-2 cursor-pointer">
      {Icon && <Icon size={16} />}
      {label}
    </label> 
  </div> 
);

// Componente NumericInput
const NumericInput = ({ label, name, value, onChange, disabled, min = 0, unit = '' }) => (
  <div className="form-group col-span-2 md:col-span-1">
    <label htmlFor={name}>{label}</label>
    <div className="flex items-center gap-2">
      <input 
        type="number" 
        id={name}
        name={name}
        value={value || 0}
        onChange={onChange} 
        className="form-input" 
        disabled={disabled}
        min={min}
      />
      {unit && <span className="text-sm text-gray-500 whitespace-nowrap">{unit}</span>}
    </div>
  </div>
);

const PlansContasFormPage = () => {
  const { id: planoId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEditing = !!planoId;

  const { data: planoData, isLoading: pageLoading, error: planError } = useContaPlanById(planoId);
  const { data: accountsData, isLoading: subscriptionsLoading, error: accountsError } = useAccountsForPlan(planoId);
  const { createPlan, updatePlan, isCreating, isUpdating } = useMutateContaPlans();
  const { user } = useAuth();
  const { getDefaultFeatures, validatePlanData } = usePlanFeatures();

  // Vindo do botão "Ver contas vinculadas" da listagem (PlansContasPage) —
  // abre já na aba certa em vez de sempre cair em "Detalhes".
  const [activeTab, setActiveTab] = useState(location.state?.openTab || 'detalhes');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    isActive: true,
    trialPeriodDays: 0,
    isDefaultTrial: false, 
    paymentMethods: ['credit_card', 'boleto'],
    features: getDefaultFeatures()
  });
  const [formErrors, setFormErrors] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);

  // ✅ CORREÇÃO: Carregar dados do plano
  useEffect(() => {
    if (isEditing && planoData && !isInitialized) {
      console.log('📥 Carregando dados do plano para edição:', planoData);
      
      const loadedFeatures = { 
        ...getDefaultFeatures(), 
        ...(planoData.features || {}) 
      };

      // Garantir estrutura correta para limites
      Object.keys(loadedFeatures).forEach(key => {
        if (key.startsWith('limite') && loadedFeatures[key]) {
          if (typeof loadedFeatures[key] === 'object') {
            loadedFeatures[key] = {
              value: loadedFeatures[key].value || 0,
              isUnlimited: !!loadedFeatures[key].isUnlimited
            };
          } else if (typeof loadedFeatures[key] === 'number') {
            loadedFeatures[key] = {
              value: loadedFeatures[key],
              isUnlimited: false
            };
          }
        }
      });

      // Formatar preço corretamente
      const priceInReais = planoData.price ? (planoData.price / 100).toFixed(2).replace('.', ',') : '0,00';

      setFormData({
        name: planoData.name || '',
        description: planoData.description || '',
        price: priceInReais,
        isActive: planoData.isActive !== false,
        trialPeriodDays: planoData.trialPeriodDays || 0,
        isDefaultTrial: !!planoData.isDefaultTrial,
        paymentMethods: planoData.paymentMethods || ['credit_card', 'boleto'],
        features: loadedFeatures
      });
      
      setIsInitialized(true);
    }
  }, [planoData, isEditing, getDefaultFeatures, isInitialized]);

  // ✅ CORREÇÃO: Resetar inicialização quando mudar de plano
  useEffect(() => {
    setIsInitialized(false);
  }, [planoId]);

  // ✅ CORREÇÃO CRÍTICA: Processamento correto dos dados das contas
  const processAccountsData = useCallback(() => {
    if (!accountsData) {
      return {
        accounts: [],
        statistics: {
          totalAccounts: 0,
          activeSubscriptions: 0,
          trialAccounts: 0,
          totalRevenueInReais: 0
        }
      };
    }

    // ✅ CORREÇÃO: A estrutura pode variar - tratar diferentes formatos
    let accountsArray = [];
    let stats = {
      totalAccounts: 0,
      activeSubscriptions: 0,
      trialAccounts: 0,
      totalRevenueInReais: 0
    };

    // Verificar diferentes estruturas possíveis
    if (Array.isArray(accountsData.data)) {
      accountsArray = accountsData.data;
      stats.totalAccounts = accountsData.count || accountsData.total || accountsArray.length;
    } else if (accountsData.data && Array.isArray(accountsData.data.accounts)) {
      accountsArray = accountsData.data.accounts;
      stats.totalAccounts = accountsData.data.count || accountsData.data.total || accountsArray.length;
    } else if (Array.isArray(accountsData)) {
      accountsArray = accountsData;
      stats.totalAccounts = accountsArray.length;
    }

    // ✅ CORREÇÃO: Processar estatísticas das contas
    accountsArray.forEach(account => {
      const status = account.subscription?.status || account.status;
      
      if (status === 'active') {
        stats.activeSubscriptions++;
      } else if (status === 'trialing') {
        stats.trialAccounts++;
      }

      // Calcular receita se disponível
      if (account.subscription?.plan?.price) {
        stats.totalRevenueInReais += (account.subscription.plan.price / 100);
      }
    });

    return {
      accounts: accountsArray,
      statistics: stats
    };
  }, [accountsData]);

  const { accounts: processedAccounts, statistics } = processAccountsData();

  console.log('📊 Dados processados das contas:', {
    accountsData,
    processedAccounts: processedAccounts.map(acc => ({
      id: acc._id,
      name: acc.name,
      nomeFantasia: acc.nomeFantasia,
      franquia: acc.franquia,
      status: acc.status,
      subscription: acc.subscription,
      email: acc.email,
      telefone: acc.telefone
    })),
    statistics
  });

  // ✅ CORREÇÃO CRÍTICA: Usar totalRevenueInReais correto do backend
  const getCorrectRevenue = useCallback(() => {
    // ✅ PRIORIDADE 1: Usar statistics.totalRevenueInReais do backend
    if (statistics.totalRevenueInReais !== undefined && statistics.totalRevenueInReais !== 0) {
      console.log('💰 Usando receita do backend:', statistics.totalRevenueInReais);
      return statistics.totalRevenueInReais;
    }
    
    // ✅ PRIORIDADE 2: Calcular baseado no preço do plano e assinaturas ativas
    if (planoData?.price && statistics.activeSubscriptions) {
      const calculatedRevenue = (planoData.price / 100) * statistics.activeSubscriptions;
      console.log(`💰 Receita calculada: R$ ${calculatedRevenue.toFixed(2)} (${planoData.price / 100} × ${statistics.activeSubscriptions})`);
      return calculatedRevenue;
    }
    
    // ✅ PRIORIDADE 3: Usar totalRevenue em centavos convertido
    if (statistics.totalRevenue) {
      const revenueInReais = statistics.totalRevenue / 100;
      console.log('💰 Receita convertida de centavos:', revenueInReais);
      return revenueInReais;
    }
    
    console.log('💰 Receita padrão: 0');
    return 0;
  }, [statistics, planoData]);

  const correctRevenue = getCorrectRevenue();

  console.log('💰 Receita Total Corrigida:', {
    statistics,
    planoDataPrice: planoData?.price,
    correctRevenue,
    calculated: (planoData?.price / 100) * (statistics.activeSubscriptions || 0)
  });

  // ✅ CORREÇÃO: Funções auxiliares melhoradas
  const getAccountName = (account) => {
    return account.name || account.nome || account.nomeFantasia || 'N/A';
  };

  const getFranquiaName = (account) => {
    if (!account.franquia) return 'N/A';
    
    if (typeof account.franquia === 'object') {
      return account.franquia.nome || account.franquia.name || 'N/A';
    }
    
    return 'N/A';
  };

  const getSubscriptionStatus = (account) => {
    // ✅ CORREÇÃO: Priorizar subscription status, depois account status
    if (account.subscription?.status) {
      return account.subscription.status;
    }
    return account.status || 'N/A';
  };

  const getNextBilling = (account) => {
    if (account.subscription?.currentPeriodEnd) {
      return new Date(account.subscription.currentPeriodEnd).toLocaleDateString('pt-BR');
    }
    
    // ✅ CORREÇÃO: Tentar fallback para trialExpiresAt
    if (account.trialExpiresAt) {
      return new Date(account.trialExpiresAt).toLocaleDateString('pt-BR');
    }
    
    return 'N/A';
  };

  const formatPhone = (phone) => {
    if (!phone) return 'N/A';
    
    const cleaned = phone.replace(/\D/g, '');
    
    if (cleaned.length === 11) {
      return `(${cleaned.substring(0, 2)}) ${cleaned.substring(2, 7)}-${cleaned.substring(7)}`;
    }
    
    if (cleaned.length === 10) {
      return `(${cleaned.substring(0, 2)}) ${cleaned.substring(2, 6)}-${cleaned.substring(6)}`;
    }
    
    return phone;
  };

  // ✅ CORREÇÃO: Manipuladores de mudança
  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
    
    if (formErrors.length > 0) {
      setFormErrors([]);
    }
  }, [formErrors]);

  const handlePaymentMethodChange = useCallback((e) => {
    const { value, checked } = e.target;
    setFormData(prev => {
      const currentMethods = prev.paymentMethods || [];
      if (checked) {
        return { ...prev, paymentMethods: [...currentMethods, value] };
      } else {
        return { ...prev, paymentMethods: currentMethods.filter(method => method !== value) };
      }
    });
  }, []);

  const handleFeatureToggle = useCallback((e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      features: { 
        ...prev.features, 
        [name]: checked 
      } 
    }));
  }, []);

  const handleLimitValueChange = useCallback((e, field) => {
    const { value } = e.target;
    const numericValue = value === '' ? 0 : Number(value);
    
    setFormData(prev => ({ 
      ...prev, 
      features: { 
        ...prev.features, 
        [field]: { 
          ...(prev.features[field] || { value: 0, isUnlimited: false }),
          value: numericValue
        } 
      } 
    }));
  }, []);

  const handleUnlimitedToggle = useCallback((e, field) => {
    const { checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      features: { 
        ...prev.features, 
        [field]: { 
          ...(prev.features[field] || { value: 0, isUnlimited: false }),
          isUnlimited: checked
        } 
      } 
    }));
  }, []);

  const handleFeatureNumericChange = useCallback((e) => {
    const { name, value } = e.target;
    const numericValue = value === '' ? 0 : Number(value);
    
    setFormData(prev => ({ 
      ...prev, 
      features: { 
        ...prev.features, 
        [name]: numericValue 
      } 
    }));
  }, []);

  // ✅ CORREÇÃO: Submit function
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      setFormErrors(['Usuário não autenticado. Faça login novamente.']);
      return;
    }

    const validation = validatePlanData(formData);
    if (!validation.isValid) {
      setFormErrors(validation.errors);
      return;
    }

    const priceInCents = Math.round(parseFloat(formData.price.replace(',', '.')) * 100);
    const trialDays = Number(formData.trialPeriodDays) || 0;

    const processedFeatures = { ...formData.features };
    
    Object.keys(processedFeatures).forEach(key => {
      if (key.startsWith('limite') && processedFeatures[key]) {
        if (typeof processedFeatures[key] === 'object') {
          processedFeatures[key] = {
            value: processedFeatures[key].value || 0,
            isUnlimited: !!processedFeatures[key].isUnlimited
          };
        }
      }
    });

    const dataToSend = {
      name: formData.name.trim(),
      description: formData.description?.trim() || '',
      price: priceInCents,
      trialPeriodDays: trialDays,
      isDefaultTrial: formData.isDefaultTrial,
      isActive: formData.isActive,
      paymentMethods: formData.paymentMethods,
      features: processedFeatures
    };

    console.log('📤 Enviando dados do plano:', dataToSend);

    try {
      if (isEditing) {
        await updatePlan({ id: planoId, planData: dataToSend });
      } else {
        await createPlan(dataToSend);
      }
      
      navigate('/plataforma/planos-contas');
    } catch (error) {
      console.error('❌ Erro ao salvar plano:', error);
    }
  };

  // Estados de loading
  const isLoading = pageLoading && !isInitialized;
  const isSubmitting = isCreating || isUpdating;

  // Tratamento de erro ao carregar plano
  if (isEditing && planError) {
    return (
      <div className="page-management-container">
        <div className="alert alert-danger">
          <div>
            <strong>Erro ao carregar plano</strong>
            <p>{planError.message || 'Não foi possível carregar os dados do plano.'}</p>
          </div>
          <button 
            onClick={() => navigate('/plataforma/planos-contas')} 
            className="btn btn-outline"
          >
            Voltar
          </button>
        </div>
      </div>
    );
  }

  // Estado de carregamento
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="animate-spin text-primary" size={48} />
        <span className="ml-3 text-lg">Carregando plano...</span>
      </div>
    );
  }

  return (
    <div className="page-management-container">
      <form onSubmit={handleSubmit}>
        <PageHeader
          icon={<Package />}
          title={isEditing ? `Editar Plano: ${formData.name}` : 'Novo Plano de Conta'}
          navBack={{ to: '/plataforma/planos-contas' }}
        />

        {/* Exibir erros de validação */}
        {formErrors.length > 0 && (
          <div className="alert alert-danger mb-6">
            <strong>Erros de validação:</strong>
            <ul className="list-disc list-inside mt-2">
              {formErrors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="tabs-nav mt-6">
          <button 
            type="button" 
            className={`tab-button ${activeTab === 'detalhes' ? 'is-active' : ''}`} 
            onClick={() => setActiveTab('detalhes')}
          >
            <Package size={16} /> Detalhes
          </button>
          <button 
            type="button" 
            className={`tab-button ${activeTab === 'modulos' ? 'is-active' : ''}`} 
            onClick={() => setActiveTab('modulos')}
          >
            <CheckSquare size={16} /> Módulos
          </button>
          <button 
            type="button" 
            className={`tab-button ${activeTab === 'limites' ? 'is-active' : ''}`} 
            onClick={() => setActiveTab('limites')}
          >
            <ListChecks size={16} /> Limites
          </button>
          <button 
            type="button" 
            className={`tab-button ${activeTab === 'avancado' ? 'is-active' : ''}`} 
            onClick={() => setActiveTab('avancado')}
          >
            <Zap size={16} /> Avançado
          </button>
          {isEditing && (
            <button 
              type="button" 
              className={`tab-button ${activeTab === 'contas' ? 'is-active' : ''}`} 
              onClick={() => setActiveTab('contas')}
            >
              <Users size={16} /> Contas Vinculadas ({statistics.totalAccounts})
            </button>
          )}
        </div>

        <div className="content-panel mt-0 pt-8">
          {/* Tab Detalhes */}
          {activeTab === 'detalhes' && (
            <fieldset className="form-section" disabled={isSubmitting}>
              <legend className="form-section-title">Informações Básicas</legend>
              <div className="form-grid-container grid-cols-1 md:grid-cols-3">
                <div className="form-group">
                  <label htmlFor="name">Nome do Plano*</label>
                  <input 
                    type="text" 
                    id="name"
                    name="name" 
                    value={formData.name} 
                    onChange={handleChange} 
                    className="form-input" 
                    required 
                    placeholder="Ex: Plano Básico"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="price">Preço Mensal (R$)*</label>
                  <input 
                    type="text" 
                    id="price"
                    name="price" 
                    value={formData.price} 
                    onChange={handleChange} 
                    className="form-input" 
                    placeholder="ex: 49,90" 
                    required 
                  />
                  <div className="form-hint">
                    Valor em reais. Use vírgula para centavos.
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="trialPeriodDays">Duração do Teste Grátis (dias)</label>
                  <input 
                    type="number" 
                    id="trialPeriodDays"
                    name="trialPeriodDays" 
                    value={formData.trialPeriodDays} 
                    onChange={handleChange} 
                    className="form-input" 
                    min="0" 
                    max="365"
                  />
                </div>
                <div className="form-group full-width md:col-span-3">
                  <label htmlFor="description">Descrição</label>
                  <textarea 
                    id="description"
                    name="description" 
                    value={formData.description} 
                    onChange={handleChange} 
                    className="form-input" 
                    rows="3"
                    placeholder="Descreva os benefícios deste plano..."
                  ></textarea>
                </div>
                
                <div className="form-group full-width md:col-span-3">
                  <label>Métodos de Pagamento Permitidos</label>
                  <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-2">
                    <div className="form-group-checkbox items-center">
                      <input 
                        type="checkbox" 
                        id="payment_credit_card" 
                        value="credit_card" 
                        checked={formData.paymentMethods.includes('credit_card')} 
                        onChange={handlePaymentMethodChange} 
                        className="form-checkbox" 
                        disabled={isSubmitting}
                      />
                      <label htmlFor="payment_credit_card" className="flex items-center gap-2 cursor-pointer">
                        <CreditCard size={16} /> Cartão de Crédito
                      </label>
                    </div>
                    <div className="form-group-checkbox items-center">
                      <input 
                        type="checkbox" 
                        id="payment_boleto" 
                        value="boleto" 
                        checked={formData.paymentMethods.includes('boleto')} 
                        onChange={handlePaymentMethodChange} 
                        className="form-checkbox" 
                        disabled={isSubmitting}
                      />
                      <label htmlFor="payment_boleto" className="flex items-center gap-2 cursor-pointer">
                        <Ticket size={16} /> Boleto
                      </label>
                    </div>
                    <div className="form-group-checkbox items-center">
                      <input 
                        type="checkbox" 
                        id="payment_pix" 
                        value="pix" 
                        checked={formData.paymentMethods.includes('pix')} 
                        onChange={handlePaymentMethodChange} 
                        className="form-checkbox" 
                        disabled={isSubmitting}
                      />
                      <label htmlFor="payment_pix" className="flex items-center gap-2 cursor-pointer">
                        <Sparkles size={16} /> PIX
                      </label>
                    </div>
                  </div>
                </div>
                
                <div className="form-group-checkbox items-center full-width md:col-span-3 border-t pt-4 mt-2">
                  <input 
                    type="checkbox" 
                    name="isDefaultTrial" 
                    id="isDefaultTrial" 
                    checked={formData.isDefaultTrial} 
                    onChange={handleChange} 
                    className="form-checkbox" 
                    disabled={isSubmitting}
                  />
                  <label htmlFor="isDefaultTrial" className="cursor-pointer">
                    Plano padrão para novos testes
                  </label>
                  <p className="form-hint">
                    Marcar esta opção fará com que este seja o plano usado para todas as novas contas 
                    que iniciarem um período de teste.
                  </p>
                </div>

                {isEditing && (
                  <div className="form-group-checkbox items-center full-width md:col-span-3">
                    <input 
                      type="checkbox" 
                      name="isActive" 
                      id="isActive" 
                      checked={formData.isActive} 
                      onChange={handleChange} 
                      className="form-checkbox" 
                      disabled={isSubmitting}
                    />
                    <label htmlFor="isActive" className="cursor-pointer">Plano Ativo</label>
                    <p className="form-hint">
                      Desmarcar impedirá novas assinaturas.
                    </p>
                  </div>
                )}
              </div>
            </fieldset>
          )}
          
          {/* Tab Módulos */}
          {activeTab === 'modulos' && (
            <fieldset className="form-section" disabled={isSubmitting}>
              <legend className="form-section-title">Módulos Habilitados</legend>
              
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Package size={20} /> Funcionalidades Principais
                </h3>
                <div className="form-grid-container grid-cols-2 md:grid-cols-4">
                  <FeatureToggle 
                    label="Agenda" 
                    name="hasAgenda" 
                    checked={formData.features.hasAgenda} 
                    onChange={handleFeatureToggle}
                    disabled={isSubmitting}
                    icon={Calendar}
                  />
                  <FeatureToggle 
                    label="Recursos" 
                    name="hasRecursos" 
                    checked={formData.features.hasRecursos} 
                    onChange={handleFeatureToggle}
                    disabled={isSubmitting}
                    icon={Cpu}
                  />
                  <FeatureToggle 
                    label="Fornecedores" 
                    name="hasFornecedores" 
                    checked={formData.features.hasFornecedores} 
                    onChange={handleFeatureToggle}
                    disabled={isSubmitting}
                    icon={Users}
                  />
                  <FeatureToggle 
                    label="Serviços" 
                    name="hasServicos" 
                    checked={formData.features.hasServicos} 
                    onChange={handleFeatureToggle}
                    disabled={isSubmitting}
                    icon={Shield}
                  />
                  <FeatureToggle 
                    label="Itens de Venda" 
                    name="hasItensVenda" 
                    checked={formData.features.hasItensVenda} 
                    onChange={handleFeatureToggle}
                    disabled={isSubmitting}
                    icon={ShoppingCart}
                  />
                  <FeatureToggle 
                    label="Pacotes" 
                    name="hasPacotes" 
                    checked={formData.features.hasPacotes} 
                    onChange={handleFeatureToggle}
                    disabled={isSubmitting}
                    icon={Package}
                  />
                  <FeatureToggle 
                    label="Tipos de Contrato" 
                    name="hasTiposContrato" 
                    checked={formData.features.hasTiposContrato} 
                    onChange={handleFeatureToggle}
                    disabled={isSubmitting}
                    icon={FileText}
                  />
                  <FeatureToggle 
                    label="Contratos" 
                    name="hasContratos" 
                    checked={formData.features.hasContratos} 
                    onChange={handleFeatureToggle}
                    disabled={isSubmitting}
                    icon={FileText}
                  />
                  <FeatureToggle 
                    label="Templates" 
                    name="hasTemplates" 
                    checked={formData.features.hasTemplates} 
                    onChange={handleFeatureToggle}
                    disabled={isSubmitting}
                    icon={FileText}
                  />
                  <FeatureToggle 
                    label="Transportadores" 
                    name="hasTransportadores" 
                    checked={formData.features.hasTransportadores} 
                    onChange={handleFeatureToggle}
                    disabled={isSubmitting}
                    icon={Truck}
                  />
                  <FeatureToggle 
                    label="Galeria" 
                    name="hasGaleria" 
                    checked={formData.features.hasGaleria} 
                    onChange={handleFeatureToggle}
                    disabled={isSubmitting}
                    icon={Image}
                  />
                </div>
              </div>

              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Tags size={20} /> Novos Módulos
                </h3>
                <div className="form-grid-container grid-cols-2 md:grid-cols-4">
                  <FeatureToggle 
                    label="Carrinho" 
                    name="hasCarrinho" 
                    checked={formData.features.hasCarrinho} 
                    onChange={handleFeatureToggle}
                    disabled={isSubmitting}
                    icon={ShoppingCart}
                  />
                  <FeatureToggle 
                    label="Categorias" 
                    name="hasCategorias" 
                    checked={formData.features.hasCategorias} 
                    onChange={handleFeatureToggle}
                    disabled={isSubmitting}
                    icon={Tags}
                  />
                  <FeatureToggle 
                    label="Comunicação" 
                    name="hasComunicacao" 
                    checked={formData.features.hasComunicacao} 
                    onChange={handleFeatureToggle}
                    disabled={isSubmitting}
                    icon={MessageSquare}
                  />
                  <FeatureToggle 
                    label="Documentos Gerados" 
                    name="hasDocumentosGerados" 
                    checked={formData.features.hasDocumentosGerados} 
                    onChange={handleFeatureToggle}
                    disabled={isSubmitting}
                    icon={FileText}
                  />
                  <FeatureToggle 
                    label="Histórico Comunicação" 
                    name="hasHistoricoComunicacao" 
                    checked={formData.features.hasHistoricoComunicacao} 
                    onChange={handleFeatureToggle}
                    disabled={isSubmitting}
                    icon={Clock}
                  />
                  <FeatureToggle 
                    label="Métodos Pagamento" 
                    name="hasMetodosPagamento" 
                    checked={formData.features.hasMetodosPagamento} 
                    onChange={handleFeatureToggle}
                    disabled={isSubmitting}
                    icon={DollarSign}
                  />
                  <FeatureToggle 
                    label="Ofertas" 
                    name="hasOfertas" 
                    checked={formData.features.hasOfertas} 
                    onChange={handleFeatureToggle}
                    disabled={isSubmitting}
                    icon={Tags}
                  />
                  <FeatureToggle 
                    label="Pacotes Clientes" 
                    name="hasPacotesClientes" 
                    checked={formData.features.hasPacotesClientes} 
                    onChange={handleFeatureToggle}
                    disabled={isSubmitting}
                    icon={Package}
                  />
                  <FeatureToggle 
                    label="Payment Gateway" 
                    name="hasPaymentGateway" 
                    checked={formData.features.hasPaymentGateway} 
                    onChange={handleFeatureToggle}
                    disabled={isSubmitting}
                    icon={CreditCard}
                  />
                  <FeatureToggle 
                    label="Pagamentos" 
                    name="hasPagamentos" 
                    checked={formData.features.hasPagamentos} 
                    onChange={handleFeatureToggle}
                    disabled={isSubmitting}
                    icon={DollarSign}
                  />
                  <FeatureToggle 
                    label="Pedidos" 
                    name="hasPedidos" 
                    checked={formData.features.hasPedidos} 
                    onChange={handleFeatureToggle}
                    disabled={isSubmitting}
                    icon={ShoppingCart}
                  />
                  <FeatureToggle 
                    label="Unidades" 
                    name="hasUnidades" 
                    checked={formData.features.hasUnidades} 
                    onChange={handleFeatureToggle}
                    disabled={isSubmitting}
                    icon={MapPin}
                  />
                  <FeatureToggle 
                    label="Vendas" 
                    name="hasVendas" 
                    checked={formData.features.hasVendas} 
                    onChange={handleFeatureToggle}
                    disabled={isSubmitting}
                    icon={BarChart3}
                  />
                  <FeatureToggle 
                    label="Cursos" 
                    name="hasCursos" 
                    checked={formData.features.hasCursos} 
                    onChange={handleFeatureToggle}
                    disabled={isSubmitting}
                    icon={GraduationCap}
                  />
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <FileText size={20} /> Templates Específicos
                </h3>
                <div className="form-grid-container grid-cols-2 md:grid-cols-4">
                  <FeatureToggle 
                    label="Template Anamnese" 
                    name="hasTemplateAnamnese" 
                    checked={formData.features.hasTemplateAnamnese} 
                    onChange={handleFeatureToggle}
                    disabled={isSubmitting}
                    icon={FileText}
                  />
                  <FeatureToggle 
                    label="Template Certificado" 
                    name="hasTemplateCertificado" 
                    checked={formData.features.hasTemplateCertificado} 
                    onChange={handleFeatureToggle}
                    disabled={isSubmitting}
                    icon={FileText}
                  />
                  <FeatureToggle 
                    label="Template Contrato" 
                    name="hasTemplateContrato" 
                    checked={formData.features.hasTemplateContrato} 
                    onChange={handleFeatureToggle}
                    disabled={isSubmitting}
                    icon={FileText}
                  />
                  <FeatureToggle
                    label="Template Recibo"
                    name="hasTemplateRecibo"
                    checked={formData.features.hasTemplateRecibo}
                    onChange={handleFeatureToggle}
                    disabled={isSubmitting}
                    icon={FileText}
                  />
                </div>
              </div>

              {/* ✅ CORREÇÃO: hasFidelidade/hasListaEspera/hasNotaFiscal
                  existem no model desde que viraram plan-gated (set 2026),
                  mas este formulário nunca ganhou os toggles — não tinha
                  como ativar/desativar essas 3 features por plano, exceto
                  editando o banco direto. */}
              <div>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Star size={20} /> Fidelidade, Fila de Espera e Nota Fiscal
                </h3>
                <div className="form-grid-container grid-cols-2 md:grid-cols-4">
                  <FeatureToggle
                    label="Fidelidade"
                    name="hasFidelidade"
                    checked={formData.features.hasFidelidade}
                    onChange={handleFeatureToggle}
                    disabled={isSubmitting}
                    icon={Star}
                  />
                  <FeatureToggle
                    label="Fila de Espera"
                    name="hasListaEspera"
                    checked={formData.features.hasListaEspera}
                    onChange={handleFeatureToggle}
                    disabled={isSubmitting}
                    icon={Hourglass}
                  />
                  <FeatureToggle
                    label="Nota Fiscal"
                    name="hasNotaFiscal"
                    checked={formData.features.hasNotaFiscal}
                    onChange={handleFeatureToggle}
                    disabled={isSubmitting}
                    icon={Receipt}
                  />
                </div>
              </div>
            </fieldset>
          )}

          {/* Tab Limites */}
          {activeTab === 'limites' && (
            <fieldset className="form-section" disabled={isSubmitting}>
              <legend className="form-section-title">Limites de Quantidade por Conta</legend>
              
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-4">Limites Principais</h3>
                <div className="form-grid-container grid-cols-2 md:grid-cols-4">
                  <LimitInput 
                    label="Usuários" 
                    name="limiteUsuarios" 
                    feature={formData.features.limiteUsuarios} 
                    onValueChange={(e) => handleLimitValueChange(e, 'limiteUsuarios')} 
                    onUnlimitedChange={(e) => handleUnlimitedToggle(e, 'limiteUsuarios')} 
                    disabled={isSubmitting}
                  />
                  <LimitInput 
                    label="Recursos" 
                    name="limiteRecursos" 
                    feature={formData.features.limiteRecursos} 
                    onValueChange={(e) => handleLimitValueChange(e, 'limiteRecursos')} 
                    onUnlimitedChange={(e) => handleUnlimitedToggle(e, 'limiteRecursos')} 
                    disabled={isSubmitting}
                  />
                  <LimitInput 
                    label="Unidades" 
                    name="limiteUnidades" 
                    feature={formData.features.limiteUnidades} 
                    onValueChange={(e) => handleLimitValueChange(e, 'limiteUnidades')} 
                    onUnlimitedChange={(e) => handleUnlimitedToggle(e, 'limiteUnidades')} 
                    disabled={isSubmitting}
                  />
                  <LimitInput 
                    label="Clientes" 
                    name="limiteClientes" 
                    feature={formData.features.limiteClientes} 
                    onValueChange={(e) => handleLimitValueChange(e, 'limiteClientes')} 
                    onUnlimitedChange={(e) => handleUnlimitedToggle(e, 'limiteClientes')} 
                    disabled={isSubmitting}
                  />
                  <LimitInput 
                    label="Fornecedores" 
                    name="limiteFornecedores" 
                    feature={formData.features.limiteFornecedores} 
                    onValueChange={(e) => handleLimitValueChange(e, 'limiteFornecedores')} 
                    onUnlimitedChange={(e) => handleUnlimitedToggle(e, 'limiteFornecedores')} 
                    disabled={isSubmitting}
                  />
                  <LimitInput 
                    label="Serviços" 
                    name="limiteServicos" 
                    feature={formData.features.limiteServicos} 
                    onValueChange={(e) => handleLimitValueChange(e, 'limiteServicos')} 
                    onUnlimitedChange={(e) => handleUnlimitedToggle(e, 'limiteServicos')} 
                    disabled={isSubmitting}
                  />
                  <LimitInput 
                    label="Itens de Venda" 
                    name="limiteItensVenda" 
                    feature={formData.features.limiteItensVenda} 
                    onValueChange={(e) => handleLimitValueChange(e, 'limiteItensVenda')} 
                    onUnlimitedChange={(e) => handleUnlimitedToggle(e, 'limiteItensVenda')} 
                    disabled={isSubmitting}
                  />
                  <LimitInput 
                    label="Pacotes" 
                    name="limitePacotes" 
                    feature={formData.features.limitePacotes} 
                    onValueChange={(e) => handleLimitValueChange(e, 'limitePacotes')} 
                    onUnlimitedChange={(e) => handleUnlimitedToggle(e, 'limitePacotes')} 
                    disabled={isSubmitting}
                  />
                  <LimitInput 
                    label="Contratos" 
                    name="limiteContratos" 
                    feature={formData.features.limiteContratos} 
                    onValueChange={(e) => handleLimitValueChange(e, 'limiteContratos')} 
                    onUnlimitedChange={(e) => handleUnlimitedToggle(e, 'limiteContratos')} 
                    disabled={isSubmitting}
                  />
                  <LimitInput 
                    label="Tipos de Contrato" 
                    name="limiteTiposContrato" 
                    feature={formData.features.limiteTiposContrato} 
                    onValueChange={(e) => handleLimitValueChange(e, 'limiteTiposContrato')} 
                    onUnlimitedChange={(e) => handleUnlimitedToggle(e, 'limiteTiposContrato')} 
                    disabled={isSubmitting}
                  />
                  <LimitInput 
                    label="Templates (Total)" 
                    name="limiteTemplates" 
                    feature={formData.features.limiteTemplates} 
                    onValueChange={(e) => handleLimitValueChange(e, 'limiteTemplates')} 
                    onUnlimitedChange={(e) => handleUnlimitedToggle(e, 'limiteTemplates')} 
                    disabled={isSubmitting}
                  />
                  <LimitInput 
                    label="Transportadores" 
                    name="limiteTransportadores" 
                    feature={formData.features.limiteTransportadores} 
                    onValueChange={(e) => handleLimitValueChange(e, 'limiteTransportadores')} 
                    onUnlimitedChange={(e) => handleUnlimitedToggle(e, 'limiteTransportadores')} 
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-4">Novos Limites</h3>
                <div className="form-grid-container grid-cols-2 md:grid-cols-4">
                  <LimitInput 
                    label="Ofertas" 
                    name="limiteOfertas" 
                    feature={formData.features.limiteOfertas} 
                    onValueChange={(e) => handleLimitValueChange(e, 'limiteOfertas')} 
                    onUnlimitedChange={(e) => handleUnlimitedToggle(e, 'limiteOfertas')} 
                    disabled={isSubmitting}
                  />
                  <LimitInput 
                    label="Pacotes Clientes" 
                    name="limitePacotesClientes" 
                    feature={formData.features.limitePacotesClientes} 
                    onValueChange={(e) => handleLimitValueChange(e, 'limitePacotesClientes')} 
                    onUnlimitedChange={(e) => handleUnlimitedToggle(e, 'limitePacotesClientes')} 
                    disabled={isSubmitting}
                  />
                  <LimitInput 
                    label="Categorias" 
                    name="limiteCategorias" 
                    feature={formData.features.limiteCategorias} 
                    onValueChange={(e) => handleLimitValueChange(e, 'limiteCategorias')} 
                    onUnlimitedChange={(e) => handleUnlimitedToggle(e, 'limiteCategorias')} 
                    disabled={isSubmitting}
                  />
                  <LimitInput 
                    label="Métodos Pagamento" 
                    name="limiteMetodosPagamento" 
                    feature={formData.features.limiteMetodosPagamento} 
                    onValueChange={(e) => handleLimitValueChange(e, 'limiteMetodosPagamento')} 
                    onUnlimitedChange={(e) => handleUnlimitedToggle(e, 'limiteMetodosPagamento')} 
                    disabled={isSubmitting}
                  />
                  <LimitInput 
                    label="Cursos" 
                    name="limiteCursos" 
                    feature={formData.features.limiteCursos} 
                    onValueChange={(e) => handleLimitValueChange(e, 'limiteCursos')} 
                    onUnlimitedChange={(e) => handleUnlimitedToggle(e, 'limiteCursos')} 
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-4">Limites de Templates Específicos</h3>
                <div className="form-grid-container grid-cols-2 md:grid-cols-4">
                  <LimitInput 
                    label="Template Anamnese" 
                    name="limiteTemplateAnamnese" 
                    feature={formData.features.limiteTemplateAnamnese} 
                    onValueChange={(e) => handleLimitValueChange(e, 'limiteTemplateAnamnese')} 
                    onUnlimitedChange={(e) => handleUnlimitedToggle(e, 'limiteTemplateAnamnese')} 
                    disabled={isSubmitting}
                  />
                  <LimitInput 
                    label="Template Certificado" 
                    name="limiteTemplateCertificado" 
                    feature={formData.features.limiteTemplateCertificado} 
                    onValueChange={(e) => handleLimitValueChange(e, 'limiteTemplateCertificado')} 
                    onUnlimitedChange={(e) => handleUnlimitedToggle(e, 'limiteTemplateCertificado')} 
                    disabled={isSubmitting}
                  />
                  <LimitInput 
                    label="Template Contrato" 
                    name="limiteTemplateContrato" 
                    feature={formData.features.limiteTemplateContrato} 
                    onValueChange={(e) => handleLimitValueChange(e, 'limiteTemplateContrato')} 
                    onUnlimitedChange={(e) => handleUnlimitedToggle(e, 'limiteTemplateContrato')} 
                    disabled={isSubmitting}
                  />
                  <LimitInput 
                    label="Template Mensagem" 
                    name="limiteTemplateMensagem" 
                    feature={formData.features.limiteTemplateMensagem} 
                    onValueChange={(e) => handleLimitValueChange(e, 'limiteTemplateMensagem')} 
                    onUnlimitedChange={(e) => handleUnlimitedToggle(e, 'limiteTemplateMensagem')} 
                    disabled={isSubmitting}
                  />
                  <LimitInput 
                    label="Template Recibo" 
                    name="limiteTemplateRecibo" 
                    feature={formData.features.limiteTemplateRecibo} 
                    onValueChange={(e) => handleLimitValueChange(e, 'limiteTemplateRecibo')} 
                    onUnlimitedChange={(e) => handleUnlimitedToggle(e, 'limiteTemplateRecibo')} 
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-4">Limites Mensais</h3>
                <div className="form-grid-container grid-cols-2 md:grid-cols-4">
                  <LimitInput 
                    label="Pedidos Mensais" 
                    name="limitePedidosMensais" 
                    feature={formData.features.limitePedidosMensais} 
                    onValueChange={(e) => handleLimitValueChange(e, 'limitePedidosMensais')} 
                    onUnlimitedChange={(e) => handleUnlimitedToggle(e, 'limitePedidosMensais')} 
                    disabled={isSubmitting}
                  />
                  <LimitInput 
                    label="Vendas Mensais" 
                    name="limiteVendasMensais" 
                    feature={formData.features.limiteVendasMensais} 
                    onValueChange={(e) => handleLimitValueChange(e, 'limiteVendasMensais')} 
                    onUnlimitedChange={(e) => handleUnlimitedToggle(e, 'limiteVendasMensais')} 
                    disabled={isSubmitting}
                  />
                  <LimitInput 
                    label="Agendamentos Mensais" 
                    name="limiteAgendamentosMensais" 
                    feature={formData.features.limiteAgendamentosMensais} 
                    onValueChange={(e) => handleLimitValueChange(e, 'limiteAgendamentosMensais')} 
                    onUnlimitedChange={(e) => handleUnlimitedToggle(e, 'limiteAgendamentosMensais')} 
                    disabled={isSubmitting}
                  />
                  <LimitInput 
                    label="Comunicações Mensais" 
                    name="limiteComunicacoesMensais" 
                    feature={formData.features.limiteComunicacoesMensais} 
                    onValueChange={(e) => handleLimitValueChange(e, 'limiteComunicacoesMensais')} 
                    onUnlimitedChange={(e) => handleUnlimitedToggle(e, 'limiteComunicacoesMensais')} 
                    disabled={isSubmitting}
                  />
                </div>
              </div>
            </fieldset>
          )}

          {/* Tab Avançado */}
          {activeTab === 'avancado' && (
            <fieldset className="form-section" disabled={isSubmitting}>
              <legend className="form-section-title">Configurações Avançadas</legend>
              
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Zap size={20} /> Features Avançadas
                </h3>
                <div className="form-grid-container grid-cols-2 md:grid-cols-4">
                  <FeatureToggle 
                    label="Relatórios Avançados" 
                    name="hasRelatoriosAvancados" 
                    checked={formData.features.hasRelatoriosAvancados} 
                    onChange={handleFeatureToggle}
                    disabled={isSubmitting}
                    icon={BarChart3}
                  />
                  <FeatureToggle 
                    label="Integração WhatsApp" 
                    name="hasIntegracaoWhatsApp" 
                    checked={formData.features.hasIntegracaoWhatsApp} 
                    onChange={handleFeatureToggle}
                    disabled={isSubmitting}
                    icon={Smartphone}
                  />
                  <FeatureToggle
                    label="Integração Email"
                    name="hasIntegracaoEmail"
                    checked={formData.features.hasIntegracaoEmail}
                    onChange={handleFeatureToggle}
                    disabled={isSubmitting}
                    icon={Mail}
                  />
                  {/* ✅ CORREÇÃO: hasIntegracaoSMS/hasCanalCentralizado existiam
                      no model, nunca tiveram controle neste formulário */}
                  <FeatureToggle
                    label="Integração SMS"
                    name="hasIntegracaoSMS"
                    checked={formData.features.hasIntegracaoSMS}
                    onChange={handleFeatureToggle}
                    disabled={isSubmitting}
                    icon={MessageCircle}
                  />
                  <FeatureToggle
                    label="Canal Centralizado (Matriz/Franquia)"
                    name="hasCanalCentralizado"
                    checked={formData.features.hasCanalCentralizado}
                    onChange={handleFeatureToggle}
                    disabled={isSubmitting}
                    icon={Globe}
                  />
                  <FeatureToggle
                    label="Múltiplas Moedas"
                    name="hasMultiplasMoedas" 
                    checked={formData.features.hasMultiplasMoedas} 
                    onChange={handleFeatureToggle}
                    disabled={isSubmitting}
                    icon={Coins}
                  />
                  <FeatureToggle 
                    label="Backup Automático" 
                    name="hasBackupAutomatico" 
                    checked={formData.features.hasBackupAutomatico} 
                    onChange={handleFeatureToggle}
                    disabled={isSubmitting}
                    icon={Database}
                  />
                  <FeatureToggle 
                    label="Acesso API" 
                    name="hasAPIAccess" 
                    checked={formData.features.hasAPIAccess} 
                    onChange={handleFeatureToggle}
                    disabled={isSubmitting}
                    icon={Code}
                  />
                  <FeatureToggle 
                    label="White Label" 
                    name="hasWhiteLabel" 
                    checked={formData.features.hasWhiteLabel} 
                    onChange={handleFeatureToggle}
                    disabled={isSubmitting}
                    icon={Palette}
                  />
                  <FeatureToggle
                    label="Suporte Prioritário"
                    name="hasSuportePrioritario"
                    checked={formData.features.hasSuportePrioritario}
                    onChange={handleFeatureToggle}
                    disabled={isSubmitting}
                    icon={Headphones}
                  />
                  <FeatureToggle
                    label="Home Personalizada"
                    name="hasHomePersonalizada"
                    checked={formData.features.hasHomePersonalizada}
                    onChange={handleFeatureToggle}
                    disabled={isSubmitting}
                    icon={Home}
                  />
                  <FeatureToggle
                    label="Geração de Conteúdo por IA"
                    name="hasGeracaoIA"
                    checked={formData.features.hasGeracaoIA}
                    onChange={handleFeatureToggle}
                    disabled={isSubmitting}
                    icon={Sparkles}
                  />
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-4">Configurações de Performance</h3>
                <div className="form-grid-container grid-cols-2 md:grid-cols-4">
                  <NumericInput 
                    label="Armazenamento" 
                    name="maxStorageGB" 
                    value={formData.features.maxStorageGB} 
                    onChange={handleFeatureNumericChange}
                    disabled={isSubmitting}
                    unit="GB"
                  />
                  <NumericInput 
                    label="Tamanho Máx. Arquivo" 
                    name="maxFileSizeMB" 
                    value={formData.features.maxFileSizeMB} 
                    onChange={handleFeatureNumericChange}
                    disabled={isSubmitting}
                    unit="MB"
                  />
                  <NumericInput 
                    label="Agendamentos Simultâneos" 
                    name="maxAgendamentosSimultaneos" 
                    value={formData.features.maxAgendamentosSimultaneos} 
                    onChange={handleFeatureNumericChange}
                    disabled={isSubmitting}
                  />
                  <NumericInput 
                    label="Retenção de Dados" 
                    name="retentionPeriodDays" 
                    value={formData.features.retentionPeriodDays} 
                    onChange={handleFeatureNumericChange}
                    disabled={isSubmitting}
                    unit="dias"
                  />
                </div>
              </div>
            </fieldset>
          )}
          
          {/* Tab Contas Vinculadas - CORREÇÃO DEFINITIVA APLICADA */}
          {activeTab === 'contas' && isEditing && (
            <div className="table-container">
              {accountsError ? (
                // .alert/.alert-danger so tem CSS dentro de .report-page
                // (_reports.scss, usado pelas paginas de Indicadores) — aqui
                // fora desse contexto renderizava sem nenhum estilo.
                <div className="panel-state-wrapper panel-error">
                  <AlertCircle size={24} className="icon" />
                  <strong>Erro ao carregar contas:</strong>
                  <p className="mt-1">{accountsError.message}</p>
                </div>
              ) : (
                <div className="table-wrapper">

                  {/* ✅ CORREÇÃO CRÍTICA: Seção de estatísticas com receita total corrigida */}
                  <div className="mb-4 p-4 bg-blue-50 rounded-lg">
                    <h3 className="font-semibold text-blue-800">Estatísticas do Plano</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-2">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-blue-600">{statistics.totalAccounts}</div>
                        <div className="text-sm text-blue-800">Total de Contas</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-600">
                          {statistics.activeSubscriptions}
                        </div>
                        <div className="text-sm text-green-800">Assinaturas Ativas</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-yellow-600">
                          {statistics.trialAccounts}
                        </div>
                        <div className="text-sm text-yellow-800">Contas em Trial</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-purple-600">
                          R$ {correctRevenue.toFixed(2)}
                        </div>
                        <div className="text-sm text-purple-800">Receita Total</div>
                      </div>
                    </div>
                  </div>

                  <table className="styled-table">
                    <thead>
                      <tr>
                        <th>Nome da Conta</th>
                        <th>Nome Fantasia</th>
                        <th>Franquia</th>
                        <th>Status Assinatura</th>
                        <th>Próxima Cobrança</th>
                        <th>Email</th>
                        <th>Telefone</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subscriptionsLoading ? (
                        <tr>
                          <td colSpan="7" className="text-center p-4">
                            <Loader2 className="animate-spin mx-auto" />
                            <div className="mt-2 text-sm text-gray-500">Carregando contas...</div>
                          </td>
                        </tr>
                      ) : processedAccounts.length > 0 ? (
                        processedAccounts.map(acc => (
                          <tr key={acc._id} className="hover:bg-gray-50">
                            <td className="font-medium">
                              {getAccountName(acc)}
                            </td>
                            <td>
                              {acc.nomeFantasia || 'N/A'}
                            </td>
                            <td>
                              {getFranquiaName(acc)}
                            </td>
                            <td>
                              {/* badge-secondary nao existe (so .badge base,
                                  sem cor nenhuma) — badge-gray e o fallback
                                  neutro usado no resto do app */}
                              <span className={`badge ${
                                getSubscriptionStatus(acc) === 'active' ? 'badge-success' :
                                getSubscriptionStatus(acc) === 'trialing' ? 'badge-warning' :
                                getSubscriptionStatus(acc) === 'canceled' ? 'badge-danger' : 'badge-gray'
                              }`}>
                                {getSubscriptionStatus(acc).toUpperCase()}
                              </span>
                            </td>
                            <td>
                              {getNextBilling(acc)}
                            </td>
                            <td>
                              {acc.email ? (
                                <a 
                                  href={`mailto:${acc.email}`} 
                                  className="text-blue-600 hover:text-blue-800 hover:underline"
                                  title={`Enviar email para ${acc.email}`}
                                >
                                  {acc.email}
                                </a>
                              ) : (
                                'N/A'
                              )}
                            </td>
                            <td>
                              {acc.telefone ? (
                                <a 
                                  href={`https://wa.me/55${acc.telefone.replace(/\D/g, '')}`} 
                                  className="text-green-600 hover:text-green-800 hover:underline"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title={`Abrir WhatsApp para ${formatPhone(acc.telefone)}`}
                                >
                                  {formatPhone(acc.telefone)}
                                </a>
                              ) : (
                                'N/A'
                              )}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="7" className="text-center p-8 text-gray-500">
                            <Users size={48} className="mx-auto mb-4 text-gray-300" />
                            <div className="text-lg font-medium">Nenhuma conta vinculada</div>
                            <div className="text-sm">Este plano ainda não possui contas associadas.</div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        <FabSaveButton loading={isSubmitting} label="Salvar Plano" />
      </form>
    </div>
  );
};

export default PlansContasFormPage;